<template>
  <div class="modal fade" :id="modalId" tabindex="-1" role="dialog" aria-labelledby="statementsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-capitalize" id="statementsModalLabel">
            Account Statement{{ unit && unit.unitNo ? ` - Unit: ${unit.unitNo}` : '' }}{{ unit && unit.occupants ? ` (${unit.occupants})` : '' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-info" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3">Loading statement...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="alert alert-danger" role="alert">
            <i class="mdi mdi-alert-circle me-2"></i>
            {{ error }}
          </div>

          <!-- Statement Content -->
          <div v-else>
            <!-- Drift Warning: units.arrears disagrees with arrears_history -->
            <div v-if="summary.hasDrift" class="alert alert-warning d-flex align-items-start mb-3" role="alert">
              <i class="mdi mdi-alert-outline me-2 mt-1" style="font-size: 20px;"></i>
              <div class="flex-grow-1">
                <strong>Account out of sync.</strong>
                The stored arrears on this unit (KES {{ numberFormat(summary.storedArrears) }})
                do not match the arrears history (KES {{ numberFormat(summary.totalOutstanding) }}).
                <span :class="summary.drift > 0 ? 'text-danger' : 'text-success'">
                  Drift: {{ summary.drift > 0 ? '+' : '' }}KES {{ numberFormat(Math.abs(summary.drift)) }}
                </span>.
                <div class="small text-muted mt-1">
                  The figures shown below are derived from the arrears history (source of truth).
                  Run reconciliation to correct <code>units.arrears</code>.
                </div>
              </div>
            </div>

            <!-- Opening Balance Notice -->
            <div v-if="summary.migratedArrears > 0" class="alert alert-info d-flex align-items-center mb-3" role="alert">
              <i class="mdi mdi-database-import me-2" style="font-size: 20px;"></i>
              <div>
                <strong>Opening balance included:</strong>
                KES {{ numberFormat(summary.migratedArrears) }}
                carried over from account setup.
              </div>
            </div>

            <!-- Summary Cards -->
            <div class="row mb-4">
              <div class="col-md-4">
                <div class="card border-primary">
                  <div class="card-body">
                    <h6 class="text-muted mb-2">Total Billed</h6>
                    <h4 class="mb-0 text-primary">KES {{ numberFormat(summary.totalBilled) }}</h4>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card border-success">
                  <div class="card-body">
                    <h6 class="text-muted mb-2">Total Paid</h6>
                    <h4 class="mb-0 text-success">KES {{ numberFormat(summary.totalPaid) }}</h4>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card border-danger">
                  <div class="card-body">
                    <h6 class="text-muted mb-2">Outstanding Balance</h6>
                    <h4 class="mb-0 text-danger">KES {{ numberFormat(summary.totalOutstanding) }}</h4>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabs for Arrears and Payments -->
            <ul class="nav nav-tabs" role="tablist">
              <li class="nav-item">
                <a class="nav-link active" data-bs-toggle="tab" href="#arrears-tab" role="tab">
                  <i class="mdi mdi-calendar-clock me-1"></i>
                  Arrears History ({{ arrears.length }})
                </a>
              </li>

              <li class="nav-item d-none">
                <a class="nav-link" data-bs-toggle="tab" href="#payments-tab" role="tab">
                  <i class="mdi mdi-cash-multiple me-1"></i>
                  Payment History ({{ payments.length }})
                </a>
              </li>
            </ul>

            <!-- Tab Content -->
            <div class="tab-content mt-3">
              <!-- Arrears Tab -->
              <div class="tab-pane fade show active" id="arrears-tab" role="tabpanel">
                <div v-if="arrears.length === 0" class="text-center py-4 text-muted">
                  <i class="mdi mdi-check-circle-outline" style="font-size: 48px;"></i>
                  <p class="mt-2">No outstanding arrears</p>
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-bordered table-striped table-sm">
                    <thead class="table-light">
                    <tr>
                      <th>Billing Period</th>
                      <th>Month</th>
                      <th>Year</th>
                      <th class="text-end">Amount Billed</th>
                      <th class="text-end">Amount Paid</th>
                      <th class="text-end">Outstanding</th>
                      <th>Status</th>
                      <th>Payment Date</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(arrear, index) in arrears" :key="index">
                      <td>
                        {{ arrear.billing_period }}
                        <span v-if="isMigrated(arrear)"
                              class="badge badge-soft-info ms-1"
                              title="Opening balance — carried over at account setup">
                          OPENING BAL
                        </span>
                      </td>
                      <td>{{ getMonthName(arrear.billing_month) }}</td>
                      <td>{{ arrear.billing_year }}</td>
                      <td class="text-end">{{ numberFormat(arrear.amount) }}</td>
                      <td class="text-end text-success">{{ numberFormat(arrear.paid_amount || 0) }}</td>
                      <td class="text-end text-danger">{{ numberFormat(arrear.outstanding) }}</td>
                      <td>
                          <span class="badge" :class="getStatusBadgeClass(arrear.status)">
                            {{ arrear.status }}
                          </span>
                      </td>
                      <td>{{ arrear.payment_date ? formatDate(arrear.payment_date) : '-' }}</td>
                    </tr>
                    </tbody>
                    <tfoot class="table-light">
                    <tr>
                      <th colspan="3" class="text-end">Totals:</th>
                      <th class="text-end">{{ numberFormat(summary.totalBilled) }}</th>
                      <th class="text-end text-success">{{ numberFormat(summary.totalPaid) }}</th>
                      <th class="text-end text-danger">{{ numberFormat(summary.totalOutstanding) }}</th>
                      <th colspan="2"></th>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <!-- Payments Tab -->
              <div class="tab-pane fade" id="payments-tab" role="tabpanel">
                <div v-if="payments.length === 0" class="text-center py-4 text-muted">
                  <i class="mdi mdi-cash-remove" style="font-size: 48px;"></i>
                  <p class="mt-2">No payment history found</p>
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-bordered table-striped table-sm">
                    <thead class="table-light">
                    <tr>
                      <th>Payment Date</th>
                      <th>Receipt No</th>
                      <th>Transaction Code</th>
                      <th>Payment Mode</th>
                      <th>Payer Name</th>
                      <th>Payer Phone</th>
                      <th class="text-end">Amount Paid</th>
                      <th>Recorded By</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(payment, index) in payments" :key="index">
                      <td>{{ payment.formatted_date || formatDate(payment.payment_date) }}</td>
                      <td>{{ payment.receipt_no }}</td>
                      <td>{{ payment.transaction_code || '-' }}</td>
                      <td>
                          <span class="badge" :class="getPaymentModeBadgeClass(payment.payment_mode)">
                            {{ payment.payment_mode || 'N/A' }}
                          </span>
                      </td>
                      <td>{{ payment.payer_name || '-' }}</td>
                      <td>{{ payment.payer_phone || '-' }}</td>
                      <td class="text-end text-success fw-bold">{{ numberFormat(payment.amount_paid) }}</td>
                      <td>{{ payment.recorded_by || '-' }}</td>
                    </tr>
                    </tbody>
                    <tfoot class="table-light">
                    <tr>
                      <th colspan="6" class="text-end">Total Payments:</th>
                      <th class="text-end text-success fw-bold">{{ numberFormat(totalPayments) }}</th>
                      <th></th>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <!-- Export Options -->
            <div class="mt-4 d-flex justify-content-end">

              <button @click="exportToCSV" class="btn btn-success">
                <i class="mdi mdi-file-delimited me-1"></i> Export CSV
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { execute, rent } from "@/api";

const DEFAULT_SUMMARY = {
  migratedArrears: 0,
  historyBilled: 0,
  totalBilled: 0,
  totalPaid: 0,
  totalOutstanding: 0,
  storedArrears: 0,
  creditBalance: 0,
  totalPayments: 0,
  drift: 0,
  hasDrift: false
};

export default {
  name: "StatementsModal",
  props: {
    unit: {
      type: Object,
      required: false,
      default: () => ({
        id: null,
        unitNo: '',
        occupants: '',
        property: '',
        phoneNumber: '',
        category: '',
        monthlyRent: 0,
        arrears: 0
      })
    },
    modalId: {
      type: String,
      default: "statements-modal"
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      arrears: [],
      payments: [],
      summary: { ...DEFAULT_SUMMARY }
    };
  },
  computed: {
    totalPayments() {
      return this.payments.reduce((sum, payment) => {
        return sum + parseFloat(payment.amount_paid || 0);
      }, 0);
    }
  },
  methods: {
    loadStatement() {
      if (!this.unit || !this.unit.id) {
        this.error = "No unit selected";
        return;
      }

      console.log("Loading statement for unit:", this.unit.id, this.unit.unitNo);

      this.loading = true;
      this.error = null;
      this.arrears = [];
      this.payments = [];
      this.summary = { ...DEFAULT_SUMMARY };

      const data = new FormData();
      data.append("function", "getUnitArrearsBreakdown");
      data.append("unitId", this.unit.id);

      execute(data, rent)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.arrears = res.data.data.arrears || [];
              this.payments = res.data.data.payments || [];

              const apiSummary = res.data.data.summary || {};
              // Merge API summary over defaults so new/diagnostic fields exist
              // even on older API versions that don't return them.
              this.summary = {
                ...DEFAULT_SUMMARY,
                ...apiSummary,
                // Coerce numerics in case the API returns strings
                migratedArrears: parseFloat(apiSummary.migratedArrears || 0),
                historyBilled: parseFloat(apiSummary.historyBilled || 0),
                totalBilled: parseFloat(apiSummary.totalBilled || 0),
                totalPaid: parseFloat(apiSummary.totalPaid || 0),
                totalOutstanding: parseFloat(apiSummary.totalOutstanding || 0),
                storedArrears: parseFloat(apiSummary.storedArrears || 0),
                creditBalance: parseFloat(apiSummary.creditBalance || 0),
                totalPayments: parseFloat(apiSummary.totalPayments || 0),
                drift: parseFloat(apiSummary.drift || 0),
                hasDrift: !!apiSummary.hasDrift
              };
            } else {
              this.error = res.data.message || "Failed to load statement";
            }
          })
          .catch((e) => {
            this.loading = false;
            this.error = e.message || "An error occurred while loading the statement";
          });
    },

    getMonthName(monthNumber) {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return months[parseInt(monthNumber) - 1] || monthNumber;
    },

    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "Invalid Date";

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    },

    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num || 0);
    },

    isMigrated(arrear) {
      // is_migrated comes back as "1" / 1 / true from the API
      return arrear && (arrear.is_migrated === 1 || arrear.is_migrated === "1" || arrear.is_migrated === true);
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'PAID':
          return 'badge-soft-success';
        case 'PARTIAL':
          return 'badge-soft-info';
        case 'UNPAID':
          return 'badge-soft-warning';
        default:
          return 'badge-soft-secondary';
      }
    },

    getPaymentModeBadgeClass(mode) {
      switch (mode) {
        case 'MPESA':
          return 'badge-soft-success';
        case 'BANK':
          return 'badge-soft-info';
        case 'CASH':
          return 'badge-soft-primary';
        default:
          return 'badge-soft-secondary';
      }
    },

    exportToCSV() {
      // Prepare CSV content
      let csvContent = "";

      // Add header information
      csvContent += "ACCOUNT STATEMENT\n";
      csvContent += `Unit Number,${this.unit.unitNo}\n`;
      csvContent += `Tenant,${this.unit.occupants}\n`;
      csvContent += `Property,${this.unit.property}\n`;
      csvContent += `Statement Date,${new Date().toLocaleDateString()}\n`;
      csvContent += "\n";

      // Add summary
      csvContent += "SUMMARY\n";
      csvContent += `Total Billed,${this.summary.totalBilled}\n`;
      csvContent += `Total Paid,${this.summary.totalPaid}\n`;
      csvContent += `Outstanding Balance,${this.summary.totalOutstanding}\n`;
      if (this.summary.migratedArrears > 0) {
        csvContent += `Opening Balance (included above),${this.summary.migratedArrears}\n`;
      }
      if (this.summary.hasDrift) {
        csvContent += `Stored Arrears (units table),${this.summary.storedArrears}\n`;
        csvContent += `Drift (stored - history),${this.summary.drift}\n`;
      }
      csvContent += "\n";

      // Add arrears history
      csvContent += "ARREARS HISTORY\n";
      csvContent += "Billing Period,Month,Year,Amount Billed,Amount Paid,Outstanding,Status,Payment Date,Opening Balance\n";

      this.arrears.forEach(arrear => {
        csvContent += `"${arrear.billing_period}",`;
        csvContent += `"${this.getMonthName(arrear.billing_month)}",`;
        csvContent += `${arrear.billing_year},`;
        csvContent += `${arrear.amount},`;
        csvContent += `${arrear.paid_amount || 0},`;
        csvContent += `${arrear.outstanding},`;
        csvContent += `"${arrear.status}",`;
        csvContent += `"${arrear.payment_date ? this.formatDate(arrear.payment_date) : '-'}",`;
        csvContent += `"${this.isMigrated(arrear) ? 'YES' : 'NO'}"\n`;
      });

      csvContent += "\n";

      /*   // Add payment history
         csvContent += "PAYMENT HISTORY\n";
         csvContent += "Payment Date,Receipt No,Transaction Code,Payment Mode,Payer Name,Payer Phone,Amount Paid,Recorded By\n";

         this.payments.forEach(payment => {
           csvContent += `"${payment.formatted_date || this.formatDate(payment.payment_date)}",`;
           csvContent += `"${payment.receipt_no}",`;
           csvContent += `"${payment.transaction_code || '-'}",`;
           csvContent += `"${payment.payment_mode || 'N/A'}",`;
           csvContent += `"${payment.payer_name || '-'}",`;
           csvContent += `"${payment.payer_phone || '-'}",`;
           csvContent += `${payment.amount_paid},`;
           csvContent += `"${payment.recorded_by || '-'}"\n`;
         });*/

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      // Generate filename with unit number and date
      const fileName = `Statement_Unit_${this.unit.unitNo}_${new Date().toISOString().split('T')[0]}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },


    show() {
      this.loadStatement();
    }
  }
};
</script>

<style scoped>
.table th {
  white-space: nowrap;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.badge-soft-success {
  color: #0f5132;
  background-color: #d1e7dd;
  border: 1px solid #badbcc;
}

.badge-soft-warning {
  color: #664d03;
  background-color: #fff3cd;
  border: 1px solid #ffecb5;
}

.badge-soft-info {
  color: #055160;
  background-color: #cff4fc;
  border: 1px solid #b6effb;
}

.badge-soft-primary {
  color: #084298;
  background-color: #cfe2ff;
  border: 1px solid #b6d4fe;
}

.badge-soft-secondary {
  color: #41464b;
  background-color: #e2e3e5;
  border: 1px solid #d3d6d8;
}

.text-success {
  color: #198754 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.table-sm td, .table-sm th {
  padding: 0.5rem;
}

.table thead th {
  font-weight: 600;
  color: #495057;
  background-color: #f8f9fa;
}

.card {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.nav-tabs .nav-link {
  color: #495057;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  color: #0d6efd;
  font-weight: 600;
}
</style>